import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { useNavigate } from "react-router";
import { useAppDispatch, useAppSelector } from "shared/hooks";
import { RootState } from "state/rootReducer";
import { actions, TERMS_LAST_CHANGED_AT } from "state/slices/userSlice";
import { CALLBACK_ROUTE } from "./routes";

/** How long to wait before trying a failed token refresh again. */
const REFRESH_RETRY_MS = 60_000;

interface StateProps {
  refreshToken: string | null;
  expirationTime: number | null;
  userAcceptedTerms: boolean | null;
}

interface DispatchProps {
  onRefreshAccessToken: typeof actions.refreshAccessToken;
}

export type AuthorizationProps = StateProps & ResolveThunks<DispatchProps>;

export function Authorization(props: AuthorizationProps): JSX.Element {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isLoggedIn = useAppSelector((state) => state.user.accessToken !== null);
  const canRefresh = isLoggedIn && props.refreshToken !== null;

  useEffect(() => {
    if (!isLoggedIn && window.location.href.includes(CALLBACK_ROUTE)) {
      const code = new URLSearchParams(window.location.search).get("code")!;

      dispatch(actions.completeOauthFlow(code, false, navigate));
    } else if (isLoggedIn && !props.userAcceptedTerms) {
      navigate("/accept_terms");
    }
  }, [isLoggedIn, window.location.href]);

  // Keeps the Spotify token fresh. This used to be checked only when the store
  // changed, so a tab left alone never noticed its token had expired, and the
  // next request after coming back failed with a 401.
  //
  // A timer set for the expiry time does most of the work. Phones pause timers
  // while the screen is off or the tab is in the background, though, so the
  // check runs again whenever the page comes back into view.
  useEffect(() => {
    if (!canRefresh || props.expirationTime === null) {
      return;
    }

    const expiresAt = props.expirationTime;
    let timer: number | undefined;

    function refreshIfDue(): void {
      if (Date.now() < expiresAt) {
        return;
      }

      void props.onRefreshAccessToken().then((token) => {
        // On success the new expiry time re-runs this effect with a new timer.
        if (token === null) {
          window.clearTimeout(timer);
          timer = window.setTimeout(refreshIfDue, REFRESH_RETRY_MS);
        }
      });
    }

    function onVisibilityChange(): void {
      if (document.visibilityState === "visible") {
        refreshIfDue();
      }
    }

    refreshIfDue();
    timer = window.setTimeout(refreshIfDue, Math.max(expiresAt - Date.now(), 0));
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", refreshIfDue);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", refreshIfDue);
    };
  }, [canRefresh, props.expirationTime]);

  function render(): JSX.Element {
    return <div></div>;
  }

  return render();
}

function mapState(state: RootState): StateProps {
  return {
    refreshToken: state.user.refreshToken,
    expirationTime: state.user.expirationTime,
    userAcceptedTerms:
      state.user.userAcceptedTerms &&
      !!state.user.userAttemptedTermsAt &&
      state.user.userAttemptedTermsAt > TERMS_LAST_CHANGED_AT,
  };
}

const mapDispatch: DispatchProps = {
  onRefreshAccessToken: actions.refreshAccessToken,
};

export default connect(mapState, mapDispatch)(Authorization);
